import { createContext, useContext } from "react";

import type { components } from "@/core/api/schema";

/**
 * The brand being worked on (from the address), and the signed-in person's role in its team. Kept apart from
 * everything else on purpose, like the session context: a hot reload never makes a second context.
 */
export type Brand = components["schemas"]["Brand"];
export type MyBrand = components["schemas"]["MyBrand"];
export type BrandRole = MyBrand["role"];

/**
 * What each role may open, the same as the API's rules (API.md §7: Owner, Manager, Staff). The API checks
 * again on every call; this only keeps people away from screens that would answer 403.
 */
export const ACCESS = {
  /** What to cook and deliver; marking meals dispatched and delivered. */
  dispatch: ["OWNER", "MANAGER", "STAFF"],
  /** Reporting a meal the kitchen couldn't serve (the customer gets a replacement). */
  missMeal: ["OWNER", "MANAGER"],
  /** The brand's numbers: subscribers, meals, ratings (no money). */
  reports: ["OWNER", "MANAGER"],
  /** Customers' plans and meal calendars, bonus meals, and reviews. */
  customers: ["OWNER", "MANAGER"],
  /** The brand's profile, delivery and pause rules, and holidays. */
  brandSettings: ["OWNER", "MANAGER"],
  /** Earnings, payouts and the payout account. */
  money: ["OWNER"],
  /** Seeing the team and its invitations. */
  team: ["OWNER", "MANAGER"],
  /** Inviting people, changing roles, removing members. */
  manageTeam: ["OWNER"],
  /** Dishes, plans, menus and promo codes. */
  catalogue: ["OWNER", "MANAGER"],
} as const satisfies Record<string, readonly BrandRole[]>;

export type Area = keyof typeof ACCESS;

export const ROLE_LABEL: Record<BrandRole, string> = {
  OWNER: "Owner",
  MANAGER: "Manager",
  STAFF: "Staff",
};

export interface BrandScopeState {
  brand: Brand;
  role: BrandRole;
  /** Whether this person's role opens an area. */
  can(area: Area): boolean;
}

export const BrandContext = createContext<BrandScopeState | null>(null);

export function useBrand(): BrandScopeState {
  const value = useContext(BrandContext);
  if (!value) throw new Error("useBrand must be used inside a brand's pages");
  return value;
}

export function canOpen(role: BrandRole, area: Area): boolean {
  return (ACCESS[area] as readonly BrandRole[]).includes(role);
}
